// Confirmation modal for voice-detected fixed monthly commitments.
import { useState, useEffect } from "react";
import { Check, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { refetchCommitments } from "@/lib/useCommitmentsCache";

export function CommitmentConfirmModal({ open, transcript, preview, onClose, onSaved }) {
  const { t, lang } = useI18n();
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("AED");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!preview) return;
    setName(preview.name || "");
    setAmount(String(preview.amount || ""));
    setCurrency((preview.currency || "AED").toUpperCase());
  }, [preview]);

  if (!open || !preview) return null;

  const canSave = name.trim().length > 0 && Number(amount) > 0 && !busy;

  const save = async () => {
    if (!canSave) return;
    setBusy(true);
    try {
      await api.post("/commitments", {
        name: name.trim(), amount: Number(amount), currency: currency.toUpperCase(),
        category: preview.category || "other", active: true,
      });
      toast.success(`${t("saved")} · ${name.trim()} · ${Number(amount).toFixed(0)} ${currency}/${t("month")}`);
      refetchCommitments();
      window.dispatchEvent(new Event("malat:data-changed"));
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error"));
    } finally { setBusy(false); }
  };

  const inputCls = "w-full px-3 py-2.5 rounded-xl border border-border bg-card text-sm font-semibold";

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-md grid place-items-center p-3">
      <div data-testid="commitment-confirm-modal" className="w-full max-w-md neu rounded-3xl flex flex-col max-h-[92vh]">
        <div className="p-5 pb-2">
          <p className="text-xs uppercase tracking-widest text-slate-500">
            {lang === "ar" ? "التزام شهري جديد" : "New monthly commitment"}
          </p>
          {transcript && <p className="text-sm mt-1 text-slate-600 italic line-clamp-2">&ldquo;{transcript}&rdquo;</p>}
          <p className="text-xs text-slate-400 mt-1">{lang === "ar" ? "راجع قبل الحفظ" : "Review before save"}</p>
        </div>
        <div className="flex-1 overflow-auto px-5 space-y-3">
          <div>
            <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">{lang === "ar" ? "الاسم" : "Name"}</label>
            <input data-testid="commitment-name-field" className={inputCls} value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">{lang === "ar" ? "المبلغ الشهري" : "Monthly amount"}</label>
              <input data-testid="commitment-amount-field" inputMode="decimal" type="text" className={`${inputCls} tabular-nums`} value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
            <div className="w-24">
              <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">{lang === "ar" ? "العملة" : "Currency"}</label>
              <input data-testid="commitment-currency-field" className={`${inputCls} uppercase text-center`} value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase().slice(0, 3))} maxLength={3} />
            </div>
          </div>
        </div>
        <div className="p-5 pt-3 flex gap-2">
          <button data-testid="commitment-cancel-btn" onClick={onClose} disabled={busy}
            className="flex-1 py-3 rounded-2xl neu-sm neu-accent-blue font-semibold flex items-center justify-center gap-2">
            <X className="w-4 h-4" />{lang === "ar" ? "إلغاء" : "Cancel"}
          </button>
          <button data-testid="commitment-confirm-btn" onClick={save} disabled={!canSave}
            className="flex-1 py-3 rounded-2xl neu-fill-blue text-white font-semibold flex items-center justify-center gap-2 shadow-lg shadow-blue-900/30 disabled:opacity-40 disabled:cursor-not-allowed">
            <Check className="w-4 h-4" />{lang === "ar" ? "حفظ" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
